/*
 * A country, from the ISO 3166-1 alpha-2 code the API sends (the server folds
 * FIE's and EFC's IOC codes to ISO, so the client meets one scheme).
 *
 * THE FLAG IS AN EMOJI, as the language picker's are: two regional-indicator
 * letters, no image and no dependency. A platform without flag glyphs (desktop
 * Windows) shows the two letters instead, which still reads.
 *
 * THE NAME IS THE BROWSER'S, in the viewer's language (Intl.DisplayNames), so
 * no locale file carries a country list.
 */

/** The flag emoji for an ISO alpha-2 code, or "" for anything else. */
export function flagEmoji(iso2: string | null | undefined): string {
  const code = (iso2 ?? "").trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return "";
  const base = 0x1f1e6 - "A".charCodeAt(0);
  return String.fromCodePoint(base + code.charCodeAt(0), base + code.charCodeAt(1));
}

/**
 * A tournament's location line (decision 776). Outside the US it is the city
 * and the country's name in the viewer's language ("Takamatsu, Japan"), or the
 * country alone with no city; a region there is a province or prefecture a
 * reader abroad cannot place. In the US, or with no country, it is the city and
 * region exactly as before ("Des Moines, IA"). "" when nothing is known.
 */
export function formatLocation(
  city: string | null | undefined,
  region: string | null | undefined,
  country: string | null | undefined,
  locale: string,
): string {
  if (isAbroad(country)) {
    return [city, countryName(country, locale)].filter(Boolean).join(", ");
  }
  return [city, region].filter(Boolean).join(", ");
}

/** Whether a tournament's country is set and is not the US: the case that
 *  takes a country name on the location line and a flag after the title.
 *  "USA" counts as home too: the server sends ISO alpha-2, but the dev seeds
 *  store the IOC code, and a US venue must never read as abroad. */
export function isAbroad(country: string | null | undefined): boolean {
  const code = (country ?? "").trim().toUpperCase();
  return code !== "" && code !== "US" && code !== "USA";
}

/** The country's name in `locale`, falling back to the code itself. */
export function countryName(iso2: string | null | undefined, locale: string): string {
  const code = (iso2 ?? "").trim().toUpperCase();
  if (code === "") return "";
  try {
    return new Intl.DisplayNames([locale], { type: "region" }).of(code) ?? code;
  } catch {
    return code;
  }
}
